import { Box, Flex, Heading, Link } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { CONTACT, HERO_PROOF } from "./homeContent";
import { Cta, Kicker, Lead, Mono, alpha, useHomeTokens } from "./primitives";

/**
 * #hero — "Carve your own line through the digital economy".
 *
 * The asterisk after "working for your interests" is deliberate: every monthly
 * plan is a managed service and none converts into source code (/terms §2).
 * It jumps to the ownership note under the plan grid, which says so and
 * publishes the price of every exit.
 */
export const Hero = () => {
  const t = useHomeTokens();
  const line = alpha(t.primary, 0.12);
  const carve = alpha(t.text, 0.055);

  const scrollToOwnershipNote = () => {
    document.getElementById("ownership-note")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box
      as="section"
      id="hero"
      position="relative"
      overflow="hidden"
      textAlign="center"
      pt={{ base: 20, md: "140px" }}
      pb={{ base: 16, md: "120px" }}
      px={6}
      scrollMarginTop="80px"
      sx={{
        "@keyframes homeGlow": {
          "0%, 100%": { opacity: 0.55, transform: "scale(1)" },
          "50%": { opacity: 0.95, transform: "scale(1.08)" },
        },
        "@keyframes homeCarve": {
          from: { transform: "translate3d(0, 0, 0)" },
          to: { transform: "translate3d(-50%, 0, 0)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& *": { animation: "none !important" },
        },
      }}
    >
      {/* Glow */}
      <Box
        position="absolute"
        top="-20%"
        left="-20%"
        right="-20%"
        h="1000px"
        pointerEvents="none"
        bg={`radial-gradient(55% 50% at 50% 30%, ${alpha(t.primary, 0.2)} 0%, transparent 70%), radial-gradient(60% 60% at 0% 0%, ${alpha(t.primary, 0.12)} 0%, transparent 55%)`}
        animation="homeGlow 14s ease-in-out infinite"
      />
      {/* Slanted guide lines */}
      <Box
        position="absolute"
        inset={0}
        pointerEvents="none"
        opacity={0.5}
        bg={`repeating-linear-gradient(100deg, transparent 0px, transparent 58px, ${line} 59px, ${line} 60px, transparent 61px, transparent 140px)`}
        sx={{
          maskImage: "linear-gradient(to bottom, transparent, black 35%, transparent 95%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent, black 35%, transparent 95%)",
        }}
      />
      {/* The "carved" strip that drifts left */}
      <Box
        position="absolute"
        left={0}
        right={0}
        top="52%"
        h="340px"
        pointerEvents="none"
        overflow="hidden"
        sx={{
          maskImage: "radial-gradient(60% 100% at 50% 50%, black, transparent)",
          WebkitMaskImage: "radial-gradient(60% 100% at 50% 50%, black, transparent)",
        }}
      >
        <Box
          w="200%"
          h="100%"
          bg={`repeating-linear-gradient(76deg, transparent 0, transparent 34px, ${carve} 35px, ${carve} 36px)`}
          animation="homeCarve 40s linear infinite"
        />
      </Box>

      <Flex position="relative" zIndex={2} maxW="980px" mx="auto" direction="column" align="center" gap="28px">
        <Flex
          align="center"
          gap="10px"
          pl="10px"
          pr="14px"
          py="7px"
          borderWidth="1px"
          borderColor={t.primaryBorder}
          bg={t.primaryTint}
          borderRadius="full"
        >
          <Box w="6px" h="6px" borderRadius="50%" bg={t.primary} boxShadow={`0 0 10px ${t.primary}`} aria-hidden="true" />
          <Kicker>The sovereign business platform · Brisbane</Kicker>
        </Flex>

        <Heading
          as="h1"
          fontSize="clamp(2.9rem, 7.2vw, 5.4rem)"
          lineHeight="1.02"
          fontWeight="600"
          letterSpacing="-0.035em"
          maxW="900px"
          color={t.text}
        >
          Carve your own line
          <br />
          <Box as="span" color={alpha(t.text, 0.42)}>
            through the
          </Box>{" "}
          digital economy
        </Heading>

        <Lead fontSize="clamp(1.05rem, 1.6vw, 1.3rem)" maxW="680px">
          Business Builder is the sovereign business platform: your website, your CRM, your billing, your AI — trained
          on your business, working for your interests.
          <Link
            as={RouterLink}
            to="/#ownership-note"
            color={t.kicker}
            fontWeight="700"
            title="What 'working for your interests' means: these are managed-service plans and owning the code is a separate purchase with a published price. Jumps to the ownership note under the subscription plans — reading only."
            aria-label="Jump to the ownership note under the subscription plans"
            onClick={scrollToOwnershipNote}
            _hover={{ textDecoration: "none", opacity: 0.8 }}
          >
            *
          </Link>{" "}
          Built on a decade of blockchain engineering and a PhD in Blockchain Security.
        </Lead>

        <Flex wrap="wrap" gap="12px" justify="center" mt={2}>
          <Cta
            href={CONTACT.bookingPath}
            newTab
            arrow
            title="Opens Tom's booking page in a new tab — pick a time for a free consultation. Nothing is charged."
          >
            Book a free consultation
          </Cta>
          <Cta
            href="#pricing"
            variant="ghost"
            title="Jumps to the subscription plans on this page. Reading only."
          >
            See the plans
          </Cta>
        </Flex>

        <Flex wrap="wrap" gap="10px 34px" justify="center" mt="34px">
          {HERO_PROOF.map((item) => (
            <Mono key={item} fontSize="11.5px" letterSpacing=".11em" textTransform="uppercase" color={t.textFaint}>
              {item}
            </Mono>
          ))}
        </Flex>
      </Flex>
    </Box>
  );
};
